import type { EnemyManager, EnemyKind } from './enemies';
import { SFX } from './audio';

interface SpawnEntry {
  delay: number;
  kind: EnemyKind;
  combat: boolean;
}

export type WaveMod = 'HASTE' | 'SWARM' | 'SHIELDED' | 'PHANTOM' | null;
export type BossArena = 'GRAVITY' | 'MIRROR' | 'MINIONS' | 'DARKNESS' | null;

export interface WaveStats {
  kills: number;
  shotsFired: number;
  shotsHit: number;
  dmgTaken: number;
  wIdxStart: number;
  bestCombo: number;
  scoreStart: number;
  startTime?: number;
  clearTime?: number;
}

export interface WaveState {
  wave: number;
  spawnQueue: SpawnEntry[];
  spawnTimer: number;
  waveBreak: boolean;
  waveTimer: number;
  banner: { text: string; color: number; t: number } | null;
  difficulty: number;
  preview: string;
  mod: WaveMod;
  summaryT: number;
  stats: WaveStats;
  prevStats: WaveStats | null;
  bossArena: BossArena;
  minionsT: number;
  cleared?: (wave: number, noDmg: boolean) => void;
}

function blankStats(): WaveStats {
  return { kills: 0, shotsFired: 0, shotsHit: 0, dmgTaken: 0, wIdxStart: 0, bestCombo: 0, scoreStart: 0 };
}

export const wave: WaveState = {
  wave: 0,
  spawnQueue: [],
  spawnTimer: 0,
  waveBreak: false,
  waveTimer: 0,
  banner: null,
  difficulty: 1,
  preview: '',
  mod: null,
  summaryT: 0,
  stats: blankStats(),
  prevStats: null,
  bossArena: null,
  minionsT: 0,
};

export function buildWave(n: number, em: EnemyManager, weaponIdx = 0, score = 0): void {
  if (n > 1) {
    wave.prevStats = { ...wave.stats };
    wave.summaryT = 1.5;
  }
  wave.wave = n;
  wave.spawnQueue.length = 0;
  wave.spawnTimer = 0;
  wave.waveBreak = false;
  wave.stats = blankStats();
  wave.stats.wIdxStart = weaponIdx;
  wave.stats.scoreStart = score;
  wave.stats.startTime = performance.now();

  const isBoss = n % 5 === 0;
  const isMini = n % 5 === 4 && n >= 9;

  // F1-era wave modifier roll (skipped on boss waves)
  if (!isBoss && Math.random() < 0.4) {
    const mods: WaveMod[] = ['HASTE', 'SWARM', 'SHIELDED', 'PHANTOM'];
    wave.mod = mods[Math.floor(Math.random() * mods.length)];
  } else {
    wave.mod = null;
  }

  if (isBoss) {
    wave.spawnQueue.push({ delay: 60, kind: 'boss', combat: true });
    wave.banner = { text: `VAGUE ${n} — BOSS`, color: 0xff00ff, t: 180 };
    const arenas: BossArena[] = ['GRAVITY', 'MIRROR', 'MINIONS'];
    wave.bossArena = Math.random() < 0.25 ? null : arenas[Math.floor(Math.random() * arenas.length)];
    wave.minionsT = 0;
    return;
  }
  wave.bossArena = null;
  if (isMini) {
    wave.spawnQueue.push({ delay: 60, kind: 'miniboss', combat: true });
    wave.banner = { text: `VAGUE ${n} — MINI-BOSS`, color: 0xff4400, t: 180 };
  } else {
    const modLabels: Record<string, string> = {
      HASTE: 'HÂTE', SWARM: 'ESSAIM', SHIELDED: 'BLINDÉ', PHANTOM: 'FANTÔME',
    };
    const tag = wave.mod ? `  ◆ ${modLabels[wave.mod] ?? wave.mod}` : '';
    wave.banner = { text: `VAGUE ${n}${tag}`, color: 0x00ffff, t: 180 };
  }

  const swarm = wave.mod === 'SWARM' ? 2 : 1;
  const droneN = (2 + Math.floor(n * 1.2)) * swarm;
  const scoutN = Math.max(0, n - 2) * swarm;
  const fighterN = Math.max(0, n - 3);
  const gunshipN = n >= 4 ? Math.floor((n - 3) / 2) + 1 : 0;
  const bomberN = n >= 5 ? 1 + Math.floor((n - 5) / 4) : 0;
  const tankN = n >= 7 ? 1 : 0;
  const carrierN = n >= 6 ? 1 : 0;
  const stealthN = n >= 4 ? Math.floor(n / 4) : 0;
  const interceptorN = n >= 3 ? Math.floor((n - 2) / 2) : 0;
  const asteroidN = 2 + Math.floor(n / 2);

  let t = 30;
  const stagger = (count: number, kind: EnemyKind, gap: number, combat = true) => {
    for (let i = 0; i < count; i++) {
      wave.spawnQueue.push({ delay: t, kind, combat });
      t += gap;
    }
  };
  stagger(droneN, 'drone', 12);
  stagger(scoutN, 'scout', 20);
  stagger(fighterN, 'fighter', 42);
  stagger(gunshipN, 'gunship', 62);
  stagger(bomberN, 'bomber', 68);
  stagger(tankN, 'tank', 95);
  stagger(carrierN, 'carrier', 75);
  stagger(stealthN, 'stealth', 34);
  stagger(interceptorN, 'interceptor', 48);
  stagger(asteroidN, 'asteroid', 70, false);

  const parts: string[] = [];
  const add = (n: number, lbl: string) => { if (n > 0) parts.push(`${n}×${lbl}`); };
  add(droneN, 'Drone');
  add(scoutN, 'Scout');
  add(fighterN, 'Fighter');
  add(gunshipN, 'Gunship');
  add(bomberN, 'Bomber');
  add(tankN, 'Tank');
  add(carrierN, 'Carrier');
  add(stealthN, 'Stealth');
  add(interceptorN, 'Interceptor');
  wave.preview = parts.join(' · ');
  wave.difficulty = 0.65 + n * 0.12;
}

export function tickWave(dt: number, em: EnemyManager, weaponIdx = 0, score = 0): void {
  if (wave.banner) {
    wave.banner.t -= 1;
    if (wave.banner.t <= 0) wave.banner = null;
  }
  if (wave.summaryT > 0) wave.summaryT = Math.max(0, wave.summaryT - dt);
  if (wave.waveBreak) {
    wave.waveTimer -= 1;
    if (wave.waveTimer <= 0) {
      buildWave(wave.wave + 1, em, weaponIdx, score);
    }
    return;
  }
  wave.spawnTimer += 1;
  while (wave.spawnQueue.length > 0 && wave.spawnQueue[0].delay <= wave.spawnTimer) {
    const sp = wave.spawnQueue.shift()!;
    em.spawn(sp.kind, wave.difficulty, wave.mod);
    if (sp.kind === 'boss' || sp.kind === 'miniboss') SFX.bossRoar();
  }
  // F1: ignore asteroids when checking wave clear
  const combatAlive = em.enemies.filter((e) => e.alive && e.kind !== 'asteroid').length;
  const combatPending = wave.spawnQueue.filter((s) => s.combat).length;
  if (combatAlive === 0 && combatPending === 0) {
    wave.waveBreak = true;
    wave.waveTimer = 65;
    wave.banner = { text: 'VAGUE TERMINÉE', color: 0x00ff88, t: 120 };
    if (wave.stats.startTime != null) {
      wave.stats.clearTime = (performance.now() - wave.stats.startTime) / 1000;
    }
    if (wave.cleared) wave.cleared(wave.wave, wave.stats.dmgTaken === 0);
  }
}
